import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  deleteField,
  onSnapshot,
  arrayUnion,
  updateDoc,
  arrayRemove,
  limit,
} from "firebase/firestore";
import { firebase } from "../firebase-config";
import { BaseUrl, ISSUE_DOMAIN, COLLECTIONS, DOMAINURL } from "../config";
import { getLoginToken } from "../storage";
import { datetimest, getuniqueId } from "../helpers";
import axiosInstance from "../axios";
import dayjs from "dayjs";
import moment from "moment";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { orderBy } from "lodash";
import EmailTemplate from "../../app/auth/EmailTemplate";

/**
 * Get List of issue
 * @param {*} onSuccess
 * @param {*} onError
 */

function updateEmailList(employees, existingEmails, newEmails) {
  const validEmails = new Set(
    employees
      .filter((record) => record.status === false || record.deleted)
      .map((record) => record.email)
  );

  const filteredEmails = existingEmails.filter(
    (email) => !validEmails.has(email)
  );
  const emailList = Array.from(new Set([...newEmails, ...filteredEmails]));
  return emailList;
}

const collectionRef = collection(firebase, COLLECTIONS.ISSUES);

/**
 * Get List of issues
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getIssuesList(companyId, onSuccess, onError) {
  try {
    const collectionRef = collection(firebase, COLLECTIONS.ISSUES);
    const q = query(collectionRef, where("companyId", "==", companyId));

    const querySnapshot = await getDocs(q);
    let results = [];

    querySnapshot.forEach((doc) => {
      let data = doc.data();
      results.push({ id: doc.id, ...data });
    });

    onSuccess && onSuccess(results);
  } catch (error) {
    console.log("Error getting issues: ", error);
    onError && onError(error);
  }
}

export function subscribeToIssuesList(companyId, onUpdate, onError, limitCount = 500) {
  try {
    const collectionRef = collection(firebase, COLLECTIONS.ISSUES);
    const q = query(
      collectionRef, 
      where("companyId", "==", companyId),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        let results = [];
        querySnapshot.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() });
        });
        onUpdate && onUpdate(results);
      },
      (error) => {
        console.log("Error subscribing to issues: ", error);
        onError && onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.log("Error setting up subscription: ", error);
    onError && onError(error);
  }
}

export async function fetchLatestIssues(formData, onSuccess, onError) {
  const collectionRef = collection(firebase, COLLECTIONS.ISSUES);

  try {
    const { companyId } = formData;

    const q = query(
      collectionRef,
      where("companyId", "==", companyId),
      orderBy("createdOn", "desc"),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      if (onSuccess) onSuccess(null);
      return null;
    }

    const latestDoc =
      snapshot.docs
        ?.map((doc) => doc.data())
        ?.filter((doc) => typeof doc.createdOn === "number")[0] || null;

    if (onSuccess) onSuccess(latestDoc);
    return latestDoc;
  } catch (error) {
    console.error("Error getting latest issues:", error);
    if (onError) onError(error);
    return null;
  }
}

export async function getIssuesByDate(formData, onSuccess, onError) {
  try {
    const startTimestamp = dayjs(formData.startDate).valueOf();
    const endTimestamp = dayjs(formData.endDate).valueOf();

    const filteredQuery = query(
      collectionRef,
      where("createdOn", ">=", startTimestamp),
      where("createdOn", "<=", endTimestamp)
    );

    const querySnapshot = await getDocs(filteredQuery);
    let results = [];
    querySnapshot.forEach((doc) => {
      let data = doc.data();
      results.push({ id: doc.id, ...data });
    });

    onSuccess && onSuccess(results);
    return results;
  } catch (error) {
    console.log("Error getting documents: ", error);
    onError && onError(error);
  }
}

export async function getIssuesByDateandCompany(
  formData,
  empName,
  companyId,
  onSuccess,
  onError
) {
  const colRef = collection(firebase, COLLECTIONS.ISSUES);
  const startTimestamp = dayjs(formData.startDate).valueOf();
  const endTimestamp = dayjs(formData.endDate).valueOf();

  const baseConstraints = [
    where("createdOn", ">=", startTimestamp),
    where("createdOn", "<=", endTimestamp),
    where("companyId", "==", companyId),
  ];

  const results = [];
  const isAll = !empName || empName === "All";
  try {
    if (isAll) {
      const q = query(colRef, ...baseConstraints);
      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
    } else if (typeof empName === "string") {
      const q = query(
        colRef,
        ...baseConstraints,
        where("createdBy", "==", empName)
      );
      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
    }
    onSuccess && onSuccess(results);
  } catch (error) {
    console.error("Error getting documents:", error);
    onError && onError(error);
  }
}

/**
 * Get Issue detail by issue id
 * @param {*} id  : issueId
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getOneIssue(id, onSuccess, onError) {
  try {
    const docRef = doc(collectionRef, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      onError && onError({ status: false, error: "Issue not found" });
      return;
    }
    let data = docSnap.data();
    onSuccess && onSuccess({ status: true, data: { ...data, id: docSnap.id } });
  } catch (error) {
    onError && onError({ status: false, error: error });
  }
}

/**
 * Add new issue to database with attachments
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function addIssue(formData, onSuccess, onError) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;

    let allattachments = [];
    if (formData.attachments && formData.attachments.length > 0) {
      const promises = formData.attachments.map(async (file) => {
        let filename = new Date().getTime() + file.name;
        return uploadFile(file, filename);
      });
      allattachments = await Promise.all(promises);
    }

    formData.attachments = allattachments;
    const docRef = await addDoc(collectionRef, {
      ...formData,
      createdBy: currentUserId,
      createdAt: datetimest(),
    });

    const responseGroupItem = { ...formData, id: docRef.id };
    onSuccess && onSuccess({ status: true, data: responseGroupItem });
  } catch (error) {
    console.log(`ERROR: ${COLLECTIONS.ISSUES} ${error}`);
    onError && onError({ status: false, error: error });
  }
}

/**
 * Update data of particular Issue
 * @param {*} id : issueId
 * @param {*} formData : only pass Data to be updated
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function updateIssue(id, formData, onSuccess, onError) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    let documentRef = doc(collectionRef, id);
    let allattachments = [];
    if (formData.attachments && formData.attachments.length > 0) {
      const promises = formData.attachments.map(async (file) => {
        let filename = new Date().getTime() + file.name;
        return uploadFile(file, filename);
      });
      allattachments = await Promise.all(promises);
    }

    formData.attachments = allattachments;
    const docContent = {
      ...formData,
      updatedAt: datetimest(),
      updatedBy: currentUserId,
    };
    await setDoc(documentRef, docContent, { merge: true });

    onSuccess &&
      onSuccess({
        status: true,
        message: "Updated successfully",
      });
  } catch (error) {
    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Not Found" });
    } else {
      onError && onError({ status: false, error: error });
    }
  }
}

/**
 * Permanently Remove Issue from database
 * @param {*} id
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function deleteIssue(id, onSuccess, onError) {
  try {
    const documentRef = doc(collectionRef, id);
    await deleteDoc(documentRef);
    onSuccess && onSuccess({ status: true, message: "Delete successful" });
  } catch (error) {
    onError && onError({ status: false, error: error });
  }
}

/**
 * Upload file to firestore
 * @param {*} file  : file blob, object
 * @param {*} filename : name of file
 * @returns : data of file after uploaded, including download url, type, size
 */

export const uploadFile = (file, filename, companyId) => {
  return new Promise((resolve, reject) => {
    const storage = getStorage();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const storageRef = ref(storage, `${companyId}/issues/${filename}`);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload is ${progress}% done`);
      },
      (error) => {
        console.log("Upload error", error);
        reject(error);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({
          url: url,
          contentType: uploadTask.snapshot.metadata.contentType,
          name: uploadTask.snapshot.metadata.name,
          size: uploadTask.snapshot.metadata.size,
        });
      }
    );
  });
};

/**
 * Add Response to Issue  (called by admin or user)
 * @param {*} id : IssueID
 * @param {*} formData : Response Data to be saved in responses
 * @param {id:'',name:''} respondBy : Name and id of user who adding response
 * @param {*} onSuccess : success callback function
 * @param {*} onError : error callback function
 */
export async function addIssueResponse(
  id,
  formData,
  respondBy,
  issueId,
  alternativeData,
  employees,
  companyId,
  companyName,
  onSuccess,
  onError
) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const documentRef = doc(firebase, COLLECTIONS.ISSUES, id);
  const docSnapshot = await getDoc(documentRef);
  const existingData = docSnapshot.exists() ? docSnapshot.data() : {};

  const newid = getuniqueId("res");
  let allAttachments = [];

  if (
    formData.attachments &&
    Array.isArray(formData.attachments) &&
    formData.attachments.length > 0
  ) {
    try {
      const uploadPromises = formData.attachments.map(async (file) => {
        const filename = `${Date.now()}_${file.name}`;
        return await uploadFile(file, filename, companyId);
      });
      allAttachments = await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading files:", error);
      onError &&
        onError({ status: false, message: "Error uploading attachments" });
      return;
    }
  }

  formData.attachments = allAttachments;

  const emailString = String(formData.to || "").trim();
  const newEmails = emailString
    ? emailString.split(",").map((email) => email.trim())
    : [];
  const existingEmails = existingData.email || [];
  const emailList = Array.from(new Set([...existingEmails, ...newEmails]));
  const updatedEmailLists = updateEmailList(
    employees,
    existingEmails,
    newEmails
  );

  const sendFormData = {
    ...existingData.responses,
    [newid]: {
      ...formData,
      respondBy,
      respondOn: datetimest(),
    },
  };

  const paddedIssueId = `${String(issueId.match(/\d+/)?.[0]).padStart(4, "0")}${
    issueId.match(/\((.*?)\)/)?.[0] || ""
  }`;

  const docContent = {
    status: "Open",
    email: emailList,
    responses: sendFormData,
    updatedAt: datetimest(),
    updatedBy: currentUserId,
    ...(formData.escalated && {
      status: formData.escalatedTo,
      escalatedDate: datetimest(),
      escalated_to_admin: formData.escalatedTo === "Escalate",
    }),
  };

  const url = `${ISSUE_DOMAIN}${id}`;

  try {
    await setDoc(documentRef, docContent, { merge: true });

    if (emailString) {
      const { subject, content } = EmailTemplate({
        subject: `ProTeams - Issue: ${paddedIssueId} (${alternativeData.clientNames} of ${companyName})`,
        content: `
          <p>Newest Message - Issue ${paddedIssueId}</p>

          <p>${alternativeData?.currentMessage}</p>

          <br/><br/><br/><br/>

          ${
            alternativeData?.previousMessage
              ? `
                <hr/>
                <p><strong>#############</strong></p>
                <p>Previous Message - Issue ${paddedIssueId}</p>

                <p>${alternativeData.previousMessage}</p>

                <br/>
              `
              : ""
          }

          ${
            alternativeData.publicPage === "Yes"
              ? `
                <p>
                  To see a full list of messages on this issue, please use this <a href='${url}' target='_blank' style="color: blue;">link</a>.
                </p>`
              : ""
          }

          <p style="color: red;">
            Please note, replies to this email will be forwarded to all parties currently associated with this ticket.
          </p>
        `,
      });

      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

      axiosInstance.post(
        `${BaseUrl}sendEmailData`,
        {
          email: updatedEmailLists?.join(", "),
          subject,
          contenttxt: content,
          issues: true,
        },
        {
          headers: {
            "Content-Type": "application/json",
            withCredentials: true,
            ...authHeaders,
          },
        }
      );
    }

    onSuccess &&
      onSuccess({
        status: true,
        message: "Updated successfully",
        response: docContent,
      });
  } catch (error) {
    console.error("Error updating document:", error);
    onError &&
      onError({
        status: false,
        message:
          error.code === "not-found"
            ? "Not Found"
            : "An error occurred while updating the document",
        error,
      });
  }
}

/**
 * Close Issue by setting status to Closed
 * @param {*} id : issueId
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function closeIssueApi(
  id,
  issueId,
  email,
  userIds,
  onSuccess,
  onError
) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const docRef = doc(collectionRef, id);

  const docContent = {
    status: "Closed",
    closedOn: datetimest(),
    readUser: [currentUserId],
    closedBy: currentUserId,
    updatedAt: datetimest(),
    updatedBy: currentUserId,
  };

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      withCredentials: true,
      ...authHeaders,
    },
  };

  try {
    await setDoc(docRef, docContent, { merge: true });
    const frmPush = {
      userIds,
      title: "ProTeams - Issues",
      textMessage: `Issue ${issueId} has been Closed`,
      extData: { type: "Issue", id: id },
    };
    axiosInstance.post(`${BaseUrl}sendPushNotifications`, frmPush, axiosConfig);

    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: email?.join(", "),
        subject: `ProTeams - Issue: ${issueId}`,
        contenttxt: `
         <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">

      
        <h2 style="font-size: 20px; margin-bottom: 15px; color: #222; text-align: left;">ProTeams Message</h2>
      
       <div style="font-size: 16px; color: #333; margin-bottom: 20px; text-align: left;">
         <p>Issue #${issueId} has been Closed.</p>
          <p> You can find this issue by searching for it in Closed issues.</p>
        </div>
      
        <p style="font-size: 14px; color: #666; margin-top: 10px; text-align: left;">
          If you have any questions about using the Web Portal or need assistance getting started, 
          please don't hesitate to reach out to our support team.
         </p>
      
     <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="https://${DOMAINURL}/help/support" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
      </div>
      </div>`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );
    onSuccess && onSuccess({ status: true, message: "Updated successfully" });
  } catch (error) {
    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Not Found" });
    }
    onError && onError({ status: false, error: error.code });
  }
}

export async function archiveIssueApi(
  id,
  issueId,
  email,
  userIds,
  onSuccess,
  onError
) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const docRef = doc(collectionRef, id);

  const docContent = {
    status: "Closed",
    archive: true,
    readUser: [currentUserId],
    archiveAt: datetimest(),
    updatedAt: datetimest(),
    updatedBy: currentUserId,
  };

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      withCredentials: true,
      ...authHeaders,
    },
  };

  try {
    await setDoc(docRef, docContent, { merge: true });
    const frmPush = {
      userIds,
      title: "ProTeams - Issues",
      textMessage: `Issue ${issueId} has been Archived`,
      extData: { type: "Issue", id: id },
    };
    axiosInstance.post(`${BaseUrl}sendPushNotifications`, frmPush, axiosConfig);

    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: email?.join(", "),
        subject: `ProTeams - Issue: ${issueId}`,
        contenttxt: `
        <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
        <h2 style="font-size: 20px; margin-bottom: 15px; color: #222; text-align: left;">ProTeams Message</h2>
      
       <div style="font-size: 16px; color: #333; margin-bottom: 20px; text-align: left;">
         <p>Issue #${issueId} has been Archived.</p>
          <p> You can find this issue by searching for it in Archived issues.</p>
        </div>
      
        <p style="font-size: 14px; color: #666; margin-top: 10px; text-align: left;">
          If you have any questions about using the Web Portal or need assistance getting started, 
          please don't hesitate to reach out to our support team.
         </p>
      
     <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="https://${DOMAINURL}/help/support" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
      </div>
      </div>`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );
    onSuccess && onSuccess({ status: true, message: "Updated successfully" });
  } catch (error) {
    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Not Found" });
    }
    onError && onError({ status: false, error: error.code });
  }
}
export async function reOpenIssue(
  id,
  issueId,
  email,
  userIds,
  onSuccess,
  onError
) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const docRef = doc(collectionRef, id);

  const docContent = {
    status: "Open",
    archive: false,
    archiveAt: deleteField(),
    updatedAt: datetimest(),
    updatedBy: currentUserId,
  };

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      withCredentials: true,
      ...authHeaders,
    },
  };

  try {
    await setDoc(docRef, docContent, { merge: true });
    const frmPush = {
      userIds,
      title: "ProTeams - Issues",
      textMessage: `Issue ${issueId} has been Re-Open.`,
      extData: { type: "Issue", id: id },
    };
    axiosInstance.post(`${BaseUrl}sendPushNotifications`, frmPush, axiosConfig);

    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: email?.join(", "),
        subject: `ProTeams - Issue: ${issueId}`,
        contenttxt: `
         <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
        <h2 style="font-size: 20px; margin-bottom: 15px; color: #222; text-align: left;">ProTeams Message</h2>
      
       <div style="font-size: 16px; color: #333; margin-bottom: 20px; text-align: left;">
         <p>Issue #${issueId} has been Re-Open.</p>
          <p> You can find this issue by searching for it in active issues.</p>
        </div>
      
        <p style="font-size: 14px; color: #666; margin-top: 10px; text-align: left;">
          If you have any questions about using the Web Portal or need assistance getting started, 
          please don't hesitate to reach out to our support team.
         </p>
      
     <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="https://${DOMAINURL}/help/support" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
      </div>
      </div>`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );
    onSuccess && onSuccess({ status: true, message: "Updated successfully" });
  } catch (error) {
    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Not Found" });
    }
    onError && onError({ status: false, error: error.code });
  }
}

/**
 * Unarchive Issue by setting status to Pending
 * @param {*} id : issueId
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function unArchiveIssue(id, onSuccess, onError) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const docRef = doc(collectionRef, id);

  const docContent = {
    status: "Open",
    archive: false,
    closedOn: deleteField(),
    archiveAt: deleteField(),
    updatedAt: datetimest(),
    updatedBy: currentUserId,
  };

  try {
    await setDoc(docRef, docContent, { merge: true });
    onSuccess && onSuccess({ status: true, message: "Updated successfully" });
  } catch (error) {
    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Not Found" });
    }
    onError && onError({ status: false, error: error.code });
  }
}

/**
 * Get Issue List Count based on status
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */

// export async function getIssueListCount(formData, onSuccess, onError) {
//   const colRef = collection(firebase, COLLECTIONS.ISSUES);
//   const statuses = formData.status || [];
//   const companyId = formData.companyId || null;

//   if (statuses.length === 0) {
//     onSuccess?.([{}]);
//     return;
//   }

//   const chunkSize = 10;
//   const chunks = [];

//   for (let i = 0; i < statuses.length; i += chunkSize) {
//     chunks.push(statuses.slice(i, i + chunkSize));
//   }

//   const promises = chunks.map((chunk) => {
//     const filters = [where("status", "in", chunk)];
//     if (companyId) filters.push(where("companyId", "==", companyId));
//     return getDocs(query(colRef, ...filters));
//   });

//   try {
//     const snapshots = await Promise.all(promises);
//     const statusCount = {};

//     snapshots.flatMap(snapshot => snapshot.docs).forEach((doc) => {
//       const status = doc.data()?.status;
//       if (status) {
//         statusCount[status] = (statusCount[status] || 0) + 1;
//       }
//     });

//     statuses.forEach((status) => {
//       if (!statusCount[status]) {
//         statusCount[status] = 0;
//       }
//     });

//     onSuccess?.([statusCount]);
//   } catch (error) {
//     console.error("Error getting issue list count:", error);
//     onError?.(error);
//   }
// }

export async function getIssueListCount(formData, onSuccess, onError) {
  const colRef = collection(firebase, COLLECTIONS.ISSUES);
  const companyId = formData?.companyId || null;
  const clientId = formData?.clientId || null;
  const userId = formData?.userId || null;

  try {
    const filters = [];
    if (companyId) {
      filters.push(where("companyId", "==", companyId));
    }

    const q = filters.length > 0 ? query(colRef, ...filters) : colRef;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let unreadCount = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const readUser = data?.readUser || [];

          if (clientId && data?.client?.id !== clientId) {
            return;
          }

          if (!readUser?.includes(userId)) {
            unreadCount += 1;
          }
        });

        onSuccess?.([{ unread: unreadCount }]);
      },
      (error) => {
        console.error("subscribeIssueListCount error:", error);
        onError?.(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error("subscribeIssueListCount setup error:", error);
    onError?.(error);
    return () => {};
  }
}

/**
 * Update Bold Status for the Issue
 * @param {*} id
 * @param {*} storedData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function updateBoldStatus(
  id,
  userId,
  employeeId,
  onSuccess,
  onError
) {
  if (!id || id?.trim() === "") {
    onError && onError({ status: false, message: "Id is Missing" });
    return;
  }

  try {
    const docRef = doc(firebase, COLLECTIONS.ISSUES, id);
    if (employeeId) {
      await updateDoc(docRef, {
        readUser: [userId],
        updatedAt: datetimest(),
      });
    } else {
      await updateDoc(docRef, {
        readUser: arrayUnion(userId),
        updatedAt: datetimest(),
      });
    }
    onSuccess && onSuccess({ status: true, data: userId });
  } catch (error) {
    console.error("Error updating document:", error);
    onError && onError({ status: false, message: error.message });
  }
}

export async function getIssuesByCompany(onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.ISSUES);
    const startDate = moment().subtract(90, "days").startOf("day").toDate();
    const endDate = moment().endOf("day").toDate();

    const q = query(
      colRef,
      where("status", "in", ["Open", "Closed"]),
      where("createdOn", ">=", startDate),
      where("createdOn", "<=", endDate)
    );

    const querySnapshot = await getDocs(q);

    const issues = [];
    querySnapshot.forEach((doc) => {
      issues.push({ id: doc.id, ...doc.data() });
    });

    onSuccess && onSuccess({ status: true, data: issues });
  } catch (error) {
    onError && onError({ status: false, error });
  }
}
